import { INPUT_CLASS, LABEL_CLASS } from '@/components/common/node-view-settings-panel';
import { getExtensions } from '@/components/tiptap-editor/tiptap-editor';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { createItLabInstances, upsertItLabContentResource } from '@/lib/zybooks-api';
import { buildZyITLabPayload } from '@/lib/zybooks-parser/node-parser/zyitlab-export';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import { ExternalLinkIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { ITLAB_SECTION_ATTR, ITLAB_TAB } from './zyitlab-shared';

type VmImageOption = { label: string; index: number };

type PreviewStatus = 'idle' | 'connecting' | 'connected' | 'error';

function collectVmImageOptions(labNode: PMNode): VmImageOption[] {
    const options: VmImageOption[] = [];
    labNode.forEach(child => {
        if (child.type.name !== 'zyITLabVmImages') {
            return;
        }
        child.forEach((vm, _offset, vmIndex) => {
            if (vm.type.name !== 'zyITLabVmImage') {
                return;
            }
            const title = vm.attrs.title || vm.attrs.name || `VM ${vmIndex + 1}`;
            options.push({ label: title, index: vmIndex });
        });
    });
    return options;
}

function resolveLabNode(editor: Editor, getPos: () => number | undefined, fallback: PMNode): PMNode {
    const pos = getPos();
    if (typeof pos !== 'number') {
        return fallback;
    }
    return editor.state.doc.nodeAt(pos) ?? fallback;
}

interface ZyITLabVmPreviewProps {
    editor: Editor;
    node: PMNode;
    getPos: () => number | undefined;
    fillHeight?: boolean;
}

export function ZyITLabVmPreview({ editor, node, getPos, fillHeight = false }: ZyITLabVmPreviewProps) {
    const labGuid = node.attrs.id as string | null | undefined;
    const vmImageOptions = collectVmImageOptions(node);
    const docAttrs = useEditorState({
        editor,
        selector: ({ editor: ed }) => ({
            zybookCode: ed?.state.doc.attrs.zybookCode as string | null | undefined,
            zybookId: ed?.state.doc.attrs.zybookId as number | null | undefined,
            sectionId: ed?.state.doc.attrs.sectionId as string | null | undefined,
        }),
    });

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [streamingUrls, setStreamingUrls] = useState<string[] | null>(null);
    const [status, setStatus] = useState<PreviewStatus>('idle');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const clampedSelectedIndex = vmImageOptions.length === 0 ? 0 : Math.min(selectedIndex, vmImageOptions.length - 1);

    const iframeSrc = streamingUrls?.length ? (streamingUrls[Math.min(clampedSelectedIndex, streamingUrls.length - 1)] ?? null) : null;

    const emptyStateMessage =
        status === 'connecting'
            ? 'Connecting to VM… A new VM may take 2-3 minutes to boot up.'
            : status === 'error'
              ? 'Connect to a VM to preview it here.'
              : 'Select a VM and click Connect to preview.';

    const missingContextMessage = useMemo(() => {
        if (!labGuid) {
            return 'Save the lab to assign an ID before connecting.';
        }
        if (!docAttrs?.zybookCode || docAttrs?.zybookId == null) {
            return 'Open this section in a zybook context to connect.';
        }
        if (!docAttrs?.sectionId) {
            return 'Document section is required to connect.';
        }
        if (vmImageOptions.length === 0) {
            return 'Add at least one VM image before connecting.';
        }
        return null;
    }, [docAttrs, labGuid, vmImageOptions.length]);

    const connectDisabled = status === 'connecting' || missingContextMessage !== null;

    const handleConnect = useCallback(async () => {
        const { zybookCode, zybookId, sectionId } = editor.state.doc.attrs;
        if (!labGuid || !zybookCode || zybookId == null || !sectionId) {
            return;
        }

        setStatus('connecting');
        setErrorMessage(null);

        try {
            const labNode = resolveLabNode(editor, getPos, node);
            const { files, items, vmImages, modelSolution, parts, instructions } = buildZyITLabPayload(labNode.toJSON(), getExtensions());
            const contentResourceId = await upsertItLabContentResource({
                zybookId,
                guid: `ma-preview-${labGuid}`,
                caption: labNode.attrs.caption || '',
                instructions,
                parts,
                payload: { files, items, vmImages, modelSolution },
            });
            const response = await createItLabInstances(zybookCode, contentResourceId, { remoteGrading: true });

            if (response.different_crids_with_running_vm?.length) {
                setStatus('error');
                setErrorMessage('Another lab already has running VMs. Close that lab before connecting.');
                return;
            }

            if (!response.streaming_urls?.length) {
                setStatus('error');
                setErrorMessage('No streaming URLs were returned for this lab.');
                return;
            }

            setStreamingUrls(response.streaming_urls);
            setStatus('connected');
        } catch (error) {
            setStatus('error');
            setErrorMessage(error instanceof Error ? error.message : 'Failed to connect to VM.');
        }
    }, [editor, getPos, labGuid, node]);

    const viewportClassName = fillHeight ? 'flex-1 min-h-0' : 'h-96';

    return (
        <div
            className={cn(
                'rounded border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40',
                fillHeight && 'flex h-full min-h-0 flex-col',
            )}
            contentEditable={false}
            {...{ [ITLAB_SECTION_ATTR]: ITLAB_TAB.VM_PREVIEW }}
        >
            <div className="mb-3 flex flex-wrap items-end gap-3">
                <div className="min-w-[12rem] flex-1">
                    <label
                        className={LABEL_CLASS}
                        contentEditable={false}
                    >
                        Virtual machine
                    </label>
                    <select
                        className={INPUT_CLASS}
                        value={vmImageOptions.length > 0 ? String(clampedSelectedIndex) : ''}
                        disabled={vmImageOptions.length === 0}
                        onChange={e => setSelectedIndex(Number(e.target.value))}
                        contentEditable={false}
                    >
                        {vmImageOptions.length === 0 ? (
                            <option value="">No VM images</option>
                        ) : (
                            vmImageOptions.map(option => (
                                <option
                                    key={option.index}
                                    value={String(option.index)}
                                >
                                    {option.label}
                                </option>
                            ))
                        )}
                    </select>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={connectDisabled}
                    onClick={handleConnect}
                >
                    {status === 'connecting' ? 'Connecting…' : 'Connect'}
                </Button>
            </div>

            {missingContextMessage && status !== 'connected' && (
                <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">{missingContextMessage}</p>
            )}

            {status === 'error' && errorMessage && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{errorMessage}</p>}

            {iframeSrc ? (
                <iframe
                    src={iframeSrc}
                    title="IT Lab VM preview"
                    className={cn(
                        'w-full rounded border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-950',
                        viewportClassName,
                    )}
                />
            ) : (
                <div
                    className={cn(
                        'flex w-full items-center justify-center rounded border border-dashed border-gray-300 bg-white px-6 text-center text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-400',
                        viewportClassName,
                    )}
                >
                    {emptyStateMessage}
                </div>
            )}

            <div className="mt-2 flex justify-end">
                <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="cursor-pointer text-blue-500 hover:text-blue-600"
                    disabled={!iframeSrc}
                    onClick={() => {
                        if (iframeSrc) {
                            window.open(iframeSrc, '_blank');
                        }
                    }}
                >
                    <ExternalLinkIcon className="mr-2 h-4 w-4" /> Open in new tab
                </Button>
            </div>
        </div>
    );
}
